/**
 * Relay monitor config helpers — pure diffing for RelayBlade.
 *
 * Keeps the unit-level relay monitor payload separate from the UI component
 * so RelayBlade can save the selected monitored relays and relay polarity
 * through the shared site-relay-config endpoint. The stored enabled flag is
 * maintained as the OR of the per-relay selection: firmware command building
 * and status parsing still read relay_monitor.enabled directly, so the FE
 * keeps writing it even though the UI no longer exposes a separate switch.
 *
 * Contains:
 * - buildRelayMonitorPayload — returns relay_monitor and relay polarity diffs
 */

const normalizeRelayNumbers = relays => [...(relays ?? [])].map(Number).sort((a, b) => a - b);

const relayNumbersEqual = (left, right) => {
  const normalizedLeft = normalizeRelayNumbers(left);
  const normalizedRight = normalizeRelayNumbers(right);
  if (normalizedLeft.length !== normalizedRight.length) return false;
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

export function buildRelayMonitorPayload(values, initialValues) {
  const relayMonitor = values.relayMonitor ?? { relaysEnabled: [] };
  const initialRelayMonitor = initialValues.relayMonitor ?? { relaysEnabled: [] };
  const relayMonitorChanged = !relayNumbersEqual(relayMonitor.relaysEnabled, initialRelayMonitor.relaysEnabled);

  const relayActions = values.relays.reduce((actions, relay, index) => {
    const initialRelay = initialValues.relays[index];
    if (!initialRelay || relay.polarity === initialRelay.polarity) return actions;
    return actions.concat({
      action: 'modify',
      id: relay.id,
      secondary_relay: relay.secondaryId === 0 ? null : relay.secondaryId,
      polarity: relay.polarity,
    });
  }, []);

  const normalizedRelaysEnabled = normalizeRelayNumbers(relayMonitor.relaysEnabled);

  return {
    relayMonitor: relayMonitorChanged
      ? {
          enabled: normalizedRelaysEnabled.length > 0,
          relays_enabled: normalizedRelaysEnabled,
        }
      : null,
    relayActions,
  };
}
